import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useMyStore, useRegisterStoreMutation } from "@/hooks/queries/useStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { StoreStatus } from "@/types/enums";
import { cn } from "@/lib/utils";
import { ImageUpload } from "@/components/shared";
import { UploadFolder } from "@/services/filesService";
import { getApiErrorMessage } from "@/services/httpClient";
import {
  Store,
  MapPin,
  Truck,
  CheckCircle2,
  Clock,
  Ban,
  ArrowRight,
  ArrowLeft,
  Navigation,
  AlertTriangle,
  User,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";

interface FormState {
  ownerName: string;
  password?: string;
  confirmPassword?: string;
  storeName: string;
  description: string;
  phone: string;
  email: string;
  fullAddress: string;
  city: string;
  state: string;
  pincode: string;
  latitude: number;
  longitude: number;
  gstNumber: string;
  gstCertificateUrl: string;
  panNumber: string;
  selfieUrl: string;
  shopCertificateUrl: string;
  aadharNumber: string;
  aadharCardUrl: string;
  hasSelfDelivery: boolean;
  deliveryRadius: number;
  minOrderAmount: number;
  deliveryCharge: number;
  freeDeliveryAbove: number;
  referralCode: string;
}

const RegisterStore = () => {
  const { isAuthenticated, user, logout, signup, login, refreshUser } = useAuth();
  const { toast } = useToast();
  const registerMutation = useRegisterStoreMutation();

  const isNewAccount = !isAuthenticated;
  const totalSteps = isNewAccount ? 4 : 3;

  // Fetch myStore if authenticated
  const { data: store, isLoading: isLoadingStore, refetch } = useMyStore(isAuthenticated);

  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<FormState>({
    ownerName: "",
    password: "",
    confirmPassword: "",
    storeName: "",
    description: "",
    phone: "",
    email: "",
    fullAddress: "",
    city: "",
    state: "",
    pincode: "",
    latitude: 25.5941, // Default Patna Latitude
    longitude: 85.1376, // Default Patna Longitude
    gstNumber: "",
    gstCertificateUrl: "",
    panNumber: "",
    selfieUrl: "",
    shopCertificateUrl: "",
    aadharNumber: "",
    aadharCardUrl: "",
    hasSelfDelivery: true,
    deliveryRadius: 5,
    minOrderAmount: 0,
    deliveryCharge: 40,
    freeDeliveryAbove: 500,
    referralCode: "",
  });

  // Pre-populate phone and email from user context
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        ownerName: prev.ownerName || user.name || "",
        phone: prev.phone || user.phone || "",
        email: prev.email || user.email || "",
      }));
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: Number(value) || 0 }));
  };

  const handleCheckboxChange = (checked: boolean, id: keyof FormState) => {
    setFormData((prev) => ({ ...prev, [id]: checked }));
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData((prev) => ({
            ...prev,
            latitude: Number(position.coords.latitude.toFixed(6)),
            longitude: Number(position.coords.longitude.toFixed(6)),
          }));
          toast({
            title: "Location detected",
            description: `Latitude: ${position.coords.latitude.toFixed(4)}, Longitude: ${position.coords.longitude.toFixed(4)}`,
          });
        },
        () => {
          toast({
            title: "Location failed",
            description: "Unable to retrieve your location. Using default coordinates.",
            variant: "destructive",
          });
        }
      );
    } else {
      toast({
        title: "Geolocation unsupported",
        description: "Your browser does not support geolocation.",
        variant: "destructive",
      });
    }
  };

  // Validate form steps
  const validateStep = (checkAll = false) => {
    if (isNewAccount && (step === 1 || checkAll)) {
      if (!formData.ownerName.trim()) {
        toast({ title: "Name is required", variant: "destructive" });
        return false;
      }
      if (!formData.phone.trim()) {
        toast({ title: "Phone number is required", variant: "destructive" });
        return false;
      }
      if (!formData.email.trim()) {
        toast({ title: "Email is required", variant: "destructive" });
        return false;
      }
      if (!formData.password || formData.password.length < 6) {
        toast({ title: "Password must be at least 6 characters", variant: "destructive" });
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        toast({ title: "Passwords do not match", variant: "destructive" });
        return false;
      }
    }

    const storeDetailsStep = isNewAccount ? 2 : 1;
    if (step === storeDetailsStep || checkAll) {
      if (!formData.storeName.trim()) {
        toast({ title: "Store Name is required", variant: "destructive" });
        return false;
      }
      if (!formData.phone.trim()) {
        toast({ title: "Contact Phone number is required", variant: "destructive" });
        return false;
      }
    }

    const addressStep = isNewAccount ? 3 : 2;
    if (step === addressStep || checkAll) {
      if (!formData.fullAddress.trim()) {
        toast({ title: "Full Address is required", variant: "destructive" });
        return false;
      }
      if (!formData.city.trim()) {
        toast({ title: "City is required", variant: "destructive" });
        return false;
      }
      if (!formData.state.trim()) {
        toast({ title: "State is required", variant: "destructive" });
        return false;
      }
      if (!formData.pincode.trim()) {
        toast({ title: "Pincode is required", variant: "destructive" });
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) setStep((s) => s + 1);
  };

  const handleBack = () => {
    setStep((s) => s - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(true)) return;

    setIsSubmitting(true);
    try {
      let currentUserId = user?.id;

      // 1. If user is not authenticated, perform signup + login first
      if (isNewAccount) {
        const signupData = {
          name: formData.ownerName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          password: formData.password || "",
        };

        const registeredUser = await signup(signupData);
        if (!registeredUser) {
          throw new Error("Could not create user account. Please check details and try again.");
        }

        // Login automatically
        const loggedInUser = await login(registeredUser.username, formData.password || "");
        if (!loggedInUser) {
          throw new Error("Account created but automatic login failed. Please sign in manually.");
        }
        currentUserId = loggedInUser.id;
      }

      // 2. Submit store registration
      await registerMutation.mutateAsync({
        storeName: formData.storeName,
        description: formData.description || undefined,
        phone: formData.phone,
        email: formData.email || undefined,
        latitude: formData.latitude,
        longitude: formData.longitude,
        fullAddress: formData.fullAddress,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        gstNumber: formData.gstNumber || undefined,
        gstCertificateUrl: formData.gstCertificateUrl || undefined,
        panNumber: formData.panNumber || undefined,
        selfieUrl: formData.selfieUrl || undefined,
        shopCertificateUrl: formData.shopCertificateUrl || undefined,
        aadharNumber: formData.aadharNumber || undefined,
        aadharCardUrl: formData.aadharCardUrl || undefined,
        hasSelfDelivery: formData.hasSelfDelivery,
        deliveryRadius: formData.deliveryRadius,
        minOrderAmount: formData.minOrderAmount,
        deliveryCharge: formData.deliveryCharge,
        freeDeliveryAbove: formData.freeDeliveryAbove,
        referralCode: formData.referralCode || undefined,
      });

      toast({
        title: "Registration successful!",
        description: "Your store registration has been submitted for admin approval.",
      });

      // Refresh user context so AuthContext updates user.role to 'store' immediately
      await refreshUser();
      // Refetch store data so the "Under Review" screen shows on this page
      try { await refetch(); } catch { /* ignore — query may still be settling */ }
      // Don't navigate to /store — the pending status screen will render here
    } catch (err: any) {
      toast({
        title: "Registration failed",
        description: getApiErrorMessage(err, "An error occurred during registration. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get text description of current step
  const getStepTitle = () => {
    if (isNewAccount) {
      switch (step) {
        case 1: return "Create Account";
        case 2: return "Store Details";
        case 3: return "Address & Location";
        case 4: return "Settings & Referral";
        default: return "";
      }
    } else {
      switch (step) {
        case 1: return "Store Details";
        case 2: return "Address & Location";
        case 3: return "Settings & Referral";
        default: return "";
      }
    }
  };

  // 1. Loading State
  if (isAuthenticated && isLoadingStore) {
    return (
      <div className="container mx-auto px-4 py-16 flex flex-col items-center justify-center min-h-[60vh]">
        <Clock className="h-10 w-10 text-primary animate-spin mb-4" />
        <p className="text-muted-foreground text-sm">Loading your store details...</p>
      </div>
    );
  }

  // 3. Authenticated: Has registered store (Pending / Active / Suspended / Rejected)
  if (store) {
    if (store.status === StoreStatus.Pending) {
      return (
        <div className="container mx-auto px-4 py-16 max-w-2xl">
          <Card className="border-warning/20 bg-warning/5 overflow-hidden shadow-lg rounded-2xl">
            <div className="bg-warning/10 p-6 flex justify-center border-b border-warning/10">
              <Clock className="h-16 w-16 text-warning animate-pulse" />
            </div>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-foreground">Application Under Review</CardTitle>
              <CardDescription className="text-sm">
                Your store application is pending approval by the administration.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-xl bg-card border border-border/80 p-5 space-y-3">
                {user?.username && (
                  <div className="flex justify-between border-b pb-2 text-sm bg-primary/5 -mx-5 -mt-5 p-5 rounded-t-xl items-center">
                    <span className="font-semibold text-primary">Login Username:</span>
                    <span className="font-mono font-bold text-primary text-base bg-primary/10 px-2 py-0.5 rounded">{user.username}</span>
                  </div>
                )}
                <div className={cn("flex justify-between border-b pb-2 text-sm", user?.username && "pt-2")}>
                  <span className="text-muted-foreground">Store Name:</span>
                  <span className="font-semibold text-foreground">{store.storeName}</span>
                </div>
                <div className="flex justify-between border-b pb-2 text-sm">
                  <span className="text-muted-foreground">Registered Phone:</span>
                  <span className="font-mono text-foreground">{store.phone}</span>
                </div>
                <div className="flex justify-between border-b pb-2 text-sm">
                  <span className="text-muted-foreground">Address:</span>
                  <span className="text-right text-foreground max-w-[70%] truncate">{store.fullAddress}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-semibold text-warning">Pending Approval</span>
                </div>
              </div>

              {user?.username && (
                <div className="text-xs text-center text-muted-foreground bg-muted/60 border border-dashed rounded-xl p-3">
                  <span className="font-semibold text-foreground">Important:</span> Please write down your login username: <strong className="font-mono text-primary text-sm">{user.username}</strong>. You will need it along with your password to sign in.
                </div>
              )}

              <div className="text-xs text-muted-foreground text-center">
                Our team is currently validating your location and seller details. You will gain access to your merchant dashboard at <code className="bg-muted px-1.5 py-0.5 rounded font-semibold">/store</code> as soon as approval is granted.
              </div>
            </CardContent>
            <CardFooter className="flex justify-between bg-card border-t p-4">
              <Button variant="outline" size="sm" onClick={() => logout()}>
                Sign Out
              </Button>
              <Button size="sm" variant="outline" asChild>
                <Link to="/">Back to Homepage</Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    if (store.status === StoreStatus.Active) {
      return (
        <div className="container mx-auto px-4 py-16 max-w-xl text-center space-y-6">
          <div className="flex justify-center">
            <CheckCircle2 className="h-20 w-20 text-success" />
          </div>
          <h1 className="text-3xl font-bold">Your Store is Active!</h1>
          <p className="text-muted-foreground">
            Congratulations! Your store <strong>{store.storeName}</strong> is active and live. You can manage orders, list products, and review payouts in your dashboard.
          </p>
          <div className="flex gap-4 justify-center">
            <Button size="lg" className="bg-gradient-primary font-semibold" asChild>
              <Link to="/store">
                Go to Store Dashboard <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      );
    }

    if (store.status === StoreStatus.Suspended || store.status === StoreStatus.Rejected) {
      const isSuspended = store.status === StoreStatus.Suspended;
      return (
        <div className="container mx-auto px-4 py-16 max-w-2xl">
          <Card className="border-destructive/20 bg-destructive/5 overflow-hidden shadow-lg rounded-2xl">
            <div className="bg-destructive/10 p-6 flex justify-center border-b border-destructive/10">
              {isSuspended ? <Ban className="h-16 w-16 text-destructive" /> : <AlertTriangle className="h-16 w-16 text-destructive" />}
            </div>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-destructive">
                Store {isSuspended ? "Suspended" : "Application Rejected"}
              </CardTitle>
              <CardDescription className="text-sm">
                Access to your store dashboard has been restricted.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-center">
              {store.rejectionReason && (
                <div className="rounded-xl bg-card border border-destructive/10 p-4 text-sm text-destructive-foreground font-medium text-left">
                  <p className="font-semibold text-foreground mb-1">Reason provided:</p>
                  <p className="text-muted-foreground">{store.rejectionReason}</p>
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                If you believe this is an error or need to resolve this issue, please contact Bihari Mall support at <a href="mailto:support@biharimall.com" className="font-semibold text-primary underline">support@biharimall.com</a>.
              </p>
            </CardContent>
            <CardFooter className="flex justify-between bg-card border-t p-4">
              <Button variant="outline" size="sm" onClick={() => logout()}>
                Sign Out
              </Button>
              <Button size="sm" variant="outline" asChild>
                <Link to="/">Back to Homepage</Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }
  }

  // 4. Render Registration Form
  return (
    <div className="space-y-6">
      {/* Step Indicators */}
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center text-xs font-semibold">
          <span className="text-muted-foreground uppercase tracking-wider">Onboarding Progress</span>
          <span className="text-primary font-bold">
            Step {step} of {totalSteps}: {getStepTitle()}
          </span>
        </div>
        <div className="flex gap-1.5 h-2 w-full bg-secondary rounded-full overflow-hidden">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
            <div
              key={s}
              className={`h-full flex-1 rounded-full transition-all duration-500 ${s <= step ? "bg-primary" : "bg-muted"
                }`}
            />
          ))}
        </div>
      </div>

      <Card className="border border-border shadow-md rounded-2xl overflow-hidden bg-card">
        <form onSubmit={handleSubmit}>
          {/* Step 1: Account Information (Only for unauthenticated users) */}
          {isNewAccount && step === 1 && (
            <CardContent className="p-6 sm:p-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="ownerName" className="text-sm font-semibold">
                  Your Full Name <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="ownerName"
                    placeholder="Enter your full name"
                    className="pl-9 h-11"
                    value={formData.ownerName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-semibold">
                    Mobile Number <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="10-digit mobile number"
                    className="h-11"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold">
                    Email Address <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    className="h-11"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-semibold">
                    Password <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min. 6 characters"
                      className="h-11 pl-9 pr-10"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-semibold">
                    Confirm Password <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm password"
                      className="h-11 pl-9"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      minLength={6}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          )}

          {/* Step 2 (New) / Step 1 (Old): Store Information */}
          {((isNewAccount && step === 2) || (!isNewAccount && step === 1)) && (
            <CardContent className="p-6 sm:p-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="storeName" className="text-sm font-semibold">
                  Store Name <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Store className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="storeName"
                    placeholder="e.g. Verma Groceries & Milk Palace"
                    className="pl-9 h-11"
                    value={formData.storeName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-semibold">
                    Contact Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="10-digit mobile number"
                    className="h-11"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold">
                    Store Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="store@example.com"
                    className="h-11"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-semibold">
                  Store Description
                </Label>
                <Textarea
                  id="description"
                  placeholder="Describe your store products, specialities, or working hours..."
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>
            </CardContent>
          )}

          {/* Step 3 (New) / Step 2 (Old): Address & Location */}
          {((isNewAccount && step === 3) || (!isNewAccount && step === 2)) && (
            <CardContent className="p-6 sm:p-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="fullAddress" className="text-sm font-semibold">
                  Full Store Address <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="fullAddress"
                  placeholder="Shop No, Building, Street Address"
                  className="h-11"
                  value={formData.fullAddress}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid gap-5 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-sm font-semibold">
                    City <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="city"
                    placeholder="e.g. Patna"
                    className="h-11"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state" className="text-sm font-semibold">
                    State <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="state"
                    placeholder="e.g. Bihar"
                    className="h-11"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pincode" className="text-sm font-semibold">
                    Pincode <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="pincode"
                    placeholder="6-digit PIN"
                    className="h-11"
                    value={formData.pincode}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="bg-secondary/40 border rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold">GPS Coordinates</h3>
                    <p className="text-xs text-muted-foreground">Required for locating your store on the customer app map.</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-xs h-9"
                    onClick={handleGetCurrentLocation}
                  >
                    <Navigation className="h-3 w-3" /> Detect GPS Location
                  </Button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="latitude" className="text-xs font-medium">Latitude</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      className="h-10 font-mono"
                      value={formData.latitude}
                      onChange={(e) => setFormData(prev => ({ ...prev, latitude: Number(e.target.value) || 0 }))}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="longitude" className="text-xs font-medium">Longitude</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      className="h-10 font-mono"
                      value={formData.longitude}
                      onChange={(e) => setFormData(prev => ({ ...prev, longitude: Number(e.target.value) || 0 }))}
                      required
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          )}

          {/* Step 4 (New) / Step 3 (Old): Settings & KYC */}
          {((isNewAccount && step === 4) || (!isNewAccount && step === 3)) && (
            <CardContent className="p-6 sm:p-8 space-y-5">
              <div className="flex items-center space-x-3 rounded-xl border border-border/80 p-4 bg-secondary/20">
                <Checkbox
                  id="hasSelfDelivery"
                  checked={formData.hasSelfDelivery}
                  onCheckedChange={(checked) => handleCheckboxChange(checked === true, "hasSelfDelivery")}
                />
                <div className="space-y-0.5">
                  <Label htmlFor="hasSelfDelivery" className="text-sm font-semibold cursor-pointer">
                    We offer self-delivery
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Check this if your own staff handles delivery. Uncheck to rely on Bihari Mall courier network.
                  </p>
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="deliveryRadius" className="text-sm font-semibold">
                    Delivery Radius (in KM)
                  </Label>
                  <Input
                    id="deliveryRadius"
                    type="number"
                    min="1"
                    max="50"
                    className="h-11"
                    value={formData.deliveryRadius}
                    onChange={handleNumberChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="minOrderAmount" className="text-sm font-semibold">
                    Minimum Order Amount (₹)
                  </Label>
                  <Input
                    id="minOrderAmount"
                    type="number"
                    min="0"
                    className="h-11"
                    value={formData.minOrderAmount}
                    onChange={handleNumberChange}
                    required
                  />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="deliveryCharge" className="text-sm font-semibold">
                    Standard Delivery Charge (₹)
                  </Label>
                  <Input
                    id="deliveryCharge"
                    type="number"
                    min="0"
                    className="h-11"
                    value={formData.deliveryCharge}
                    onChange={handleNumberChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="freeDeliveryAbove" className="text-sm font-semibold">
                    Free Delivery Threshold (₹)
                  </Label>
                  <Input
                    id="freeDeliveryAbove"
                    type="number"
                    min="0"
                    className="h-11"
                    value={formData.freeDeliveryAbove}
                    onChange={handleNumberChange}
                    required
                  />
                </div>
              </div>

              <div className="border-t pt-5 mt-2 space-y-4">
                <h3 className="text-sm font-bold flex items-center gap-1.5 text-muted-foreground border-b pb-2">
                  Referral Information (Optional)
                </h3>
                <div className="space-y-2 max-w-sm">
                  <Label htmlFor="referralCode" className="text-sm font-semibold">Referrer Store Code</Label>
                  <Input
                    id="referralCode"
                    placeholder="Sponsor Store Code"
                    className="h-11 uppercase"
                    value={formData.referralCode}
                    onChange={handleChange}
                  />
                  <p className="text-xs text-muted-foreground">
                    If you were referred to the platform by another merchant, enter their store code.
                  </p>
                </div>
              </div>
            </CardContent>
          )}

          {/* Card Footer Navigation */}
          <CardFooter className="bg-secondary/20 border-t p-4 flex justify-between">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
            ) : (
              <Button type="button" variant="ghost" asChild>
                <Link to="/">Cancel</Link>
              </Button>
            )}

            {step < totalSteps ? (
              <Button type="button" onClick={handleNext} className="gap-1.5 font-semibold">
                Next <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={isSubmitting || registerMutation.isPending}
                className="bg-gradient-primary gap-1.5 font-semibold"
              >
                {isSubmitting ? "Submitting application..." : "Submit Application"}{" "}
                <CheckCircle2 className="h-4 w-4" />
              </Button>
            )}
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default RegisterStore;
